/**
 * v79.192 — Single source of truth for per-category rendering rules.
 *
 * Historical context: We had duplicate copies of these rule-sets
 * scattered across ProductCard (`NO_CONDITION_TILE`), Admin editor
 * (`NO_PRICE_CATEGORIES`), and here. That drift caused silent bugs
 * (e.g. adding a new no-price category required editing 3 files).
 * This module now owns every "should we render X for category Y?"
 * predicate. Import from here — do NOT redefine sets inline.
 *
 * Sets kept as `Set<string>` for O(1) membership checks.
 */

// ---------------------------------------------------------------------------
// New / Used condition chip on the browse-tile
// ---------------------------------------------------------------------------
// Categories where the product isn't a physical object with a
// "new-vs-used" state — services, opportunities, financial products,
// listings whose value doesn't degrade the way a phone or car does.
const NO_CONDITION_CATEGORIES = new Set([
    "services",
    "jobs",
    "job-seeker",
    "real-estate",
    "hospitals",
    "education",
    "event-management",
    "function-halls",
    "beauty-parlours",
    "iron-shop",
    "vehicle-rental",
    "home-maid",
    "pet-supplies",
    "mutual-fund-distributor",
    "insurance-distributor",
    "mutual-fund",
    "insurance",
    "food",
    "movies-ott",
    "matrimony",
    "groceries",
    "fruits-vegetables",
]);

// ---------------------------------------------------------------------------
// Price / Currency / Stock / Brand fields in the Admin listing editor
// ---------------------------------------------------------------------------
// Categories where a rupee price + brand + condition + stock number
// make no sense (e.g. matrimony profile, insurance enquiry, movie/OTT
// promo). Applies to BOTH admin- and customer-facing forms.
const NO_PRICE_CATEGORIES = new Set([
    "matrimony",
    "insurance",
    "insurance-distributor",
    "mutual-fund",
    "mutual-fund-distributor",
    "movies-ott",
]);

// ---------------------------------------------------------------------------
// Public API — predicates + raw sets (for advanced callers)
// ---------------------------------------------------------------------------
export function shouldShowConditionTag(category, meta) {
    if (!category) return false;
    if (NO_CONDITION_CATEGORIES.has(category)) return false;
    // IT software SaaS listings under the merged `it` category don't
    // have a "new / used" state; hardware does.
    if (category === "it" && meta?.it_type === "software") return false;
    return true;
}

export function shouldShowCommerceFields(category) {
    if (!category) return true;
    return !NO_PRICE_CATEGORIES.has(category);
}

export { NO_CONDITION_CATEGORIES, NO_PRICE_CATEGORIES };
